import React, { useEffect, useRef, useState } from "react";
import { NavLink, withRouter } from "react-router-dom"; // v4/v5 compatible
import AuthenticationService from "./AuthenticationService.js";

// click‑outside helper
function useOutsideClose(ref, onClose) {
  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, onClose]);
}

function HeaderComponent({ history }) {
  const isUserLoggedIn = AuthenticationService.isUserLoggedIn();

  const [menuOpen, setMenuOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [q, setQ] = useState("");

  const resourcesRef = useRef(null);
  const accountRef = useRef(null);

  useOutsideClose(resourcesRef, () => setResourcesOpen(false));
  useOutsideClose(accountRef, () => setAccountOpen(false));

  const onSearch = (e) => {
    e.preventDefault();
    const query = q.trim();
    if (query) {
      history.push(`/search?q=${encodeURIComponent(query)}`);
      setQ("");
      setMenuOpen(false);
    }
  };

  const onLogout = (e) => {
    e.preventDefault();
    try { AuthenticationService.logout?.(); } catch {}
    history.push("/login");
  };

  const closeAllMenus = () => {
    setMenuOpen(false);
    setResourcesOpen(false);
    setAccountOpen(false);
  };

  return (
    <header className="sticky-top shadow-sm">
      {/* container-fluid so the right controls can sit near the viewport edge */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-dark" role="navigation" aria-label="Main navigation">
        <div className="container-fluid px-3 px-lg-4">
          {/* Brand */}
          <NavLink exact to="/home" className="navbar-brand d-flex align-items-center text-nowrap" onClick={closeAllMenus}>
            <span className="fw-bold">Interview Dojo</span>
          </NavLink>

          {/* Toggler (controlled) */}
          <button
            className="navbar-toggler"
            type="button"
            aria-controls="tvNavbar"
            aria-expanded={menuOpen ? "true" : "false"}
            aria-label="Toggle navigation"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className="navbar-toggler-icon" />
          </button>

          {/* Collapsible content */}
          <div className={`collapse navbar-collapse ${menuOpen ? "show" : ""} w-100`} id="tvNavbar">
            {/* LEFT: nav items (no wrapping + clean gaps) */}
            <ul className="navbar-nav flex-lg-row flex-nowrap align-items-lg-center gap-lg-4 me-lg-4">
              <li className="nav-item">
                <NavLink exact to="/learn" className="nav-link text-nowrap" activeClassName="active" onClick={closeAllMenus}>
                  Road Maps
                </NavLink>
              </li>

              {/* Tech Stacks -> the Browse Topics grid on the home page (mobile-friendly, no giant dropdown) */}
              <li className="nav-item">
                <NavLink exact to="/home#topics" className="nav-link text-nowrap" activeClassName="active" onClick={closeAllMenus}>
                  Tech Stacks
                </NavLink>
              </li>

              {/* Resources dropdown (controlled) */}
              <li className="nav-item dropdown" ref={resourcesRef}>
                <button
                  type="button"
                  className="nav-link dropdown-toggle btn btn-link p-0 text-nowrap"
                  aria-expanded={resourcesOpen ? "true" : "false"}
                  aria-haspopup="true"
                  onClick={() => { setResourcesOpen((v) => !v); setAccountOpen(false); }}
                  onKeyDown={(e) => e.key === "Escape" && setResourcesOpen(false)}
                >
                  Resources
                </button>
                <ul className={`dropdown-menu ${resourcesOpen ? "show" : ""}`}>
                  <li><NavLink exact to="/systemDesign" className="dropdown-item" activeClassName="active" onClick={closeAllMenus}>System Design</NavLink></li>
                  <li><NavLink exact to="/companies" className="dropdown-item" activeClassName="active" onClick={closeAllMenus}>Company Guides</NavLink></li>
                  <li><NavLink exact to="/resumeChecker" className="dropdown-item" activeClassName="active" onClick={closeAllMenus}>Resume Checker</NavLink></li>
                  <li><NavLink exact to="/codingRound" className="dropdown-item" activeClassName="active" onClick={closeAllMenus}>Coding Interviews</NavLink></li>
                  <li><NavLink exact to="/blogs" className="dropdown-item" activeClassName="active" onClick={closeAllMenus}>Blogs</NavLink></li>
                  <li><NavLink exact to="/projects" className="dropdown-item" activeClassName="active" onClick={closeAllMenus}>Projects</NavLink></li>
                  <li><NavLink exact to="/proficiencyTest" className="dropdown-item" activeClassName="active" onClick={closeAllMenus}>Proficiency Test</NavLink></li>
                  <li><NavLink exact to="/mobileApps" className="dropdown-item" activeClassName="active" onClick={closeAllMenus}>Mobile Apps</NavLink></li>
                  <li><hr className="dropdown-divider" /></li>
                  <li><NavLink exact to="/career" className="dropdown-item" activeClassName="active" onClick={closeAllMenus}>Career</NavLink></li>
                  <li><NavLink exact to="/privacyPolicyMain" className="dropdown-item" activeClassName="active" onClick={closeAllMenus}>Privacy Policy</NavLink></li>
                </ul>
              </li>

              <li className="nav-item">
                <NavLink exact to="/proficiencyTest" className="nav-link text-nowrap" activeClassName="active" onClick={closeAllMenus}>
                  Proficiency Test
                </NavLink>
              </li>

              <li className="nav-item">
                <NavLink exact to="/career" className="nav-link text-nowrap" activeClassName="active" onClick={closeAllMenus}>
                  Career
                </NavLink>
              </li>

              {/* Quick Search label */}
              <li className="nav-item d-none d-lg-block">
                <span className="nav-link text-white-50 text-nowrap">Quick Search</span>
              </li>

              {/* Auth‑only links */}
              {isUserLoggedIn && (
                <>
                  <li className="nav-item"><NavLink exact to="/todos" className="nav-link text-nowrap" activeClassName="active" onClick={closeAllMenus}>Todos</NavLink></li>
                  <li className="nav-item"><NavLink exact to="/addQuestion/-1" className="nav-link text-nowrap" activeClassName="active" onClick={closeAllMenus}>Add Questions</NavLink></li>
                </>
              )}
            </ul>

            {/* RIGHT: controls — search (left) + login/account pushed FAR right */}
            <div className="d-lg-flex align-items-lg-center ms-lg-auto w-100 mt-2 mt-lg-0 pe-lg-2">
              {/* Search (with a bigger gap to its right) */}
              <form className="d-flex me-3 me-lg-4" role="search" onSubmit={onSearch} aria-label="Site search">
                <input
                  className="form-control form-control-sm me-2"
                  type="search"
                  placeholder="Search topics…"
                  aria-label="Search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  style={{ minWidth: 240 }}
                />
                <button className="btn btn-outline-light btn-sm" type="submit">Search</button>
              </form>

              {/* Spacer pushes the next element (login/account) to the far right */}
              <div className="flex-grow-1 d-none d-lg-block" />

              {/* Auth controls */}
              {!isUserLoggedIn && (
                <NavLink exact to="/login" className="btn btn-primary btn-sm ms-lg-auto" onClick={closeAllMenus}>
                  Login
                </NavLink>
              )}

              {isUserLoggedIn && (
                <div className="nav-item dropdown ms-lg-auto" ref={accountRef}>
                  <button
                    type="button"
                    className="nav-link dropdown-toggle btn btn-link p-0"
                    aria-expanded={accountOpen ? "true" : "false"}
                    aria-haspopup="true"
                    onClick={() => { setAccountOpen((v) => !v); setResourcesOpen(false); }}
                  >
                    Account
                  </button>
                  <ul className={`dropdown-menu dropdown-menu-end ${accountOpen ? "show" : ""}`}>
                    <li><NavLink exact to="/profile" className="dropdown-item" activeClassName="active" onClick={closeAllMenus}>Profile</NavLink></li>
                    <li><NavLink exact to="/settings" className="dropdown-item" activeClassName="active" onClick={closeAllMenus}>Settings</NavLink></li>
                    <li><hr className="dropdown-divider" /></li>
                    <li><a className="dropdown-item" href="/logout" onClick={onLogout}>Logout</a></li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}

export default withRouter(HeaderComponent);